import { useEffect, useRef, useState } from 'react';
import { sounds } from '../lib/sounds';
import { useAppStore } from '../stores/app';
import { useClientTimer } from '../hooks/useTimer';
import { AnswerButtons } from '../components/player/AnswerButtons';
import { TimerBar } from '../components/player/Timer';
import { Leaderboard } from '../components/player/Leaderboard';
import { useGameEvents } from '../hooks/useGameEvents';

export function GamePage() {
  const gameStatus = useAppStore((s) => s.gameStatus);
  const currentRound = useAppStore((s) => s.currentRound);
  const totalQuestions = useAppStore((s) => s.totalQuestions);
  const timeLeft = useAppStore((s) => s.timeLeft);
  const questionText = useAppStore((s) => s.questionText);
  const roundResults = useAppStore((s) => s.roundResults);
  const isCorrect = useAppStore((s) => s.isCorrect);
  const pointsEarned = useAppStore((s) => s.pointsEarned);
  const resultsDisplayTime = useAppStore((s) => s.resultsDisplayTime);

  // Подключаем таймер
  useClientTimer();
  const { leaveRoom } = useGameEvents();

  // Таймер для экрана результатов
  const [resultCountdown, setResultCountdown] = useState(12);
  const resultTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Звуки
  const prevTimeLeft = useRef(timeLeft);
  const prevIsCorrect = useRef(isCorrect);
  const prevGameStatus = useRef(gameStatus);

  useEffect(() => {
    // Звук при правильном/неправильном ответе
    if (isCorrect !== null && prevIsCorrect.current === null) {
      if (isCorrect) {
        sounds.playCorrect();
      } else {
        sounds.playWrong();
      }
    }
    prevIsCorrect.current = isCorrect;
  }, [isCorrect]);

  useEffect(() => {
    // Countdown звук
    if (gameStatus === "countdown" && prevGameStatus.current !== "countdown") {
      sounds.playCountdown(3);
    }
    // Новый вопрос
    if (gameStatus === "question" && prevGameStatus.current !== "question") {
      sounds.playNewQuestion();
    }
    prevGameStatus.current = gameStatus;
  }, [gameStatus]);

  useEffect(() => {
    // Тикание на последних 5 секундах
    if (gameStatus === "question") {
      const seconds = Math.ceil(timeLeft / 1000);
      const prevSeconds = Math.ceil(prevTimeLeft.current / 1000);
      if (seconds !== prevSeconds && seconds <= 5 && seconds > 0) {
        if (seconds <= 3) {
          sounds.playUrgentTick();
        } else {
          sounds.playTick();
        }
      }
    }
    prevTimeLeft.current = timeLeft;
  }, [timeLeft, gameStatus]);

  // Таймер для экрана результатов
  useEffect(() => {
    if (gameStatus === 'results') {
      setResultCountdown(resultsDisplayTime);
      resultTimerRef.current = setInterval(() => {
        setResultCountdown(prev => prev > 0 ? prev - 1 : 0);
      }, 1000);
    } else {
      if (resultTimerRef.current) {
        clearInterval(resultTimerRef.current);
      }
    }
    return () => {
      if (resultTimerRef.current) {
        clearInterval(resultTimerRef.current);
      }
    };
  }, [gameStatus]);

  // Preparing screen state
  const [prepSeconds, setPrepSeconds] = useState(0);
  const [funFact, setFunFact] = useState(0);
  const funFacts = [
    '🤖 AI генерирует уникальные вопросы...',
    '🧠 Подбираем интересные факты...',
    '⚡ Проверяем правильность ответов...',
    '🎯 Настраиваем сложность...',
    '✨ Почти готово...',
  ];

  useEffect(() => {
    if (gameStatus !== 'preparing') return;
    setPrepSeconds(0);
    setFunFact(0);
    const timer = setInterval(() => setPrepSeconds(s => s + 1), 1000);
    const factTimer = setInterval(() => setFunFact(f => f < 4 ? f + 1 : f), 4000);
    return () => { clearInterval(timer); clearInterval(factTimer); };
  }, [gameStatus]);

  if (gameStatus === 'preparing') {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center text-white p-6">
        <button onClick={leaveRoom} className="absolute top-4 left-4 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm text-white transition-all">← Выйти</button>
        <div className="text-6xl mb-6 animate-bounce">🎲</div>
        <p className="text-2xl font-bold mb-2">Готовим вопросы</p>
        <p className="text-purple-300 mb-6 text-center transition-opacity duration-500">{funFacts[funFact]}</p>
        <div className="w-64 h-2 bg-white/10 rounded-full overflow-hidden mb-4">
          <div className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-1000" style={{width: Math.min(prepSeconds * 8, 95) + '%'}} />
        </div>
        <p className="text-gray-500 text-sm">{prepSeconds} сек</p>
      </div>
    );
  }

  // Countdown
  if (gameStatus === 'countdown') {
    const seconds = Math.ceil(timeLeft / 1000);
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center text-white p-6">
        <div className="text-9xl font-bold animate-bounce">{seconds || '🚀'}</div>
        <p className="text-xl text-gray-400 mt-4">Приготовься!</p>
      </div>
    );
  }

  // Question
  if (gameStatus === 'question') {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col text-white p-6">
        <button onClick={leaveRoom} className="self-start w-8 h-8 flex items-center justify-center bg-red-500/20 hover:bg-red-500/40 rounded-full text-red-400 text-lg transition-all mb-2">✕</button>
        {/* Header */}
        <header className="text-center mb-4">
          <div className="text-sm text-gray-400 mb-2">
            Вопрос {currentRound} из {totalQuestions}
          </div>
          <TimerBar />
        </header>

        {/* Question Text */}
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold">{questionText || 'Загрузка вопроса...'}</h2>
        </div>

        {/* Answer Buttons - полные с текстом */}
        <div className="flex-1">
          <AnswerButtons />
        </div>

        {/* Timer */}
        <div className="text-center text-4xl font-bold tabular-nums mt-4">
          {Math.ceil(timeLeft / 1000)}
        </div>
      </div>
    );
  }

  // Results
  if (gameStatus === 'results') {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col text-white p-6">
        <button onClick={leaveRoom} className="self-start w-8 h-8 flex items-center justify-center bg-red-500/20 hover:bg-red-500/40 rounded-full text-red-400 text-lg transition-all mb-2">✕</button>
        {/* Result */}
        <div className="text-center mb-8">
          {isCorrect === true && (
            <>
              <div className="text-6xl mb-2">✅</div>
              <div className="text-2xl font-bold text-green-400">Правильно!</div>
              <div className="text-lg text-gray-400">+{pointsEarned} очков</div>
            </>
          )}
          {isCorrect === false && (
            <>
              <div className="text-6xl mb-2">❌</div>
              <div className="text-2xl font-bold text-red-400">Неверно</div>
              <div className="text-sm text-gray-400 mt-2">
                Правильный ответ: {roundResults?.question.options[roundResults.question.correctIndex]}
              </div>
            </>
          )}
          {isCorrect === null && (
            <>
              <div className="text-6xl mb-2">⏰</div>
              <div className="text-2xl font-bold text-gray-400">Время вышло</div>
            </>
          )}
        </div>

        {/* Explanation */}
        {roundResults?.question.explanation && (
          <div className="mb-6 p-4 bg-white/10 rounded-xl">
            <p className="text-gray-300">{roundResults.question.explanation}</p>
          </div>
        )}

        {/* Mini Leaderboard */}
        <Leaderboard compact />

        {/* Progress bar для следующего вопроса */}
        <div className="mt-auto pt-4">
          <div className="w-full bg-gray-700 rounded-full h-2 mb-2">
            <div 
              className="bg-purple-500 h-2 rounded-full transition-all duration-1000"
              style={{ width: `${((resultsDisplayTime - resultCountdown) / resultsDisplayTime) * 100}%` }}
            />
          </div>
          <div className="text-center text-gray-400 text-sm">
            Следующий вопрос через {resultCountdown} сек
          </div>
        </div>
      </div>
    );
  }

  // Fallback
  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center text-white">
      <div className="animate-spin text-4xl">⏳</div>
    </div>
  );
}